import { sdkRequest } from '@eventbrite/http';
import axios from 'axios';
import moment from 'moment';
import { formatUrl } from 'url-lib';
import { EventEntity, EventWithExpansions } from '../../constants/entities';
import { AD_STATUS } from '../PromotedListingsTable/constants';

export const getOrganizersEvents = async (
    activeOrganizationId: string,
    nameFilter?: string,
) => {
    const url = `/api/v3/organizations/${activeOrganizationId}/events/?name_filter=${
        nameFilter ? nameFilter : ''
    }&page=1&page_size=50&order_by=created_desc&status=live&series_filter=nonseries&time_filter=current_future&expand=organizer,venue,ticket_availability`;

    const response = await axios(url);

    const results = response?.data?.events || [];
    const resultsWithImages = results.filter((event: any) => !!event.logo);
    return resultsWithImages;
};

export const getEvent = async (eventId: string) => {
    try {
        const url = `/api/v3/events/${eventId}/?expand=organizer,venue,ticket_availability`;

        const response = await axios(url);

        return response.data;
    } catch (e) {
        return null;
    }
};

export const checkIfCampaignExists = async (
    activeOrganizationId: string,
    selectedEventId: string,
    callback?: any,
) => {
    const url = `/api/v3/promoted_content/organizations/${activeOrganizationId}/campaigns`;
    const data = await sdkRequest(url);
    const adCampaigns = data.campaigns;
    if (adCampaigns && adCampaigns.length > 0) {
        const promotedEvent = adCampaigns.find((campaign: any) => {
            if (!campaign.ads.length) return false;

            const ad = campaign.ads[0];
            return (
                ad.event_id === parseInt(selectedEventId) &&
                ad.status !== AD_STATUS.DELETED &&
                ad.status !== AD_STATUS.COMPLETE
            );
        });

        if (promotedEvent && history) {
            callback && callback(promotedEvent);
            return;
        } else {
            return;
        }
    } else {
        return;
    }
};

export const getCampaignByEventId = async (eventId: number | string) => {
    const url = `/api/v3/promoted_content/ads/?event_id=${eventId}`;
    const data = await sdkRequest(url);
    return data;
};

// maps v3 event objects into simpler objects for the event selector component
export const mapToEventSelectorEntities = (events?: EventWithExpansions[]) => {
    let mappedEntities: EventEntity[] = [];

    if (events && events.length > 0) {
        mappedEntities = events.map((e) => {
            return {
                name: e.name.text,
                description: e.description.text,
                image: e.logo?.url,
                url: e.url,
                id: e.id,
                start_date: e.start.local,
            };
        });
    }

    return mappedEntities;
};

export const transformToEventSelectorEvent = (event: EventWithExpansions) => {
    //The event selector component expects a simple flattened event
    const transformedEvent = {
        name: event.name.html,
        description: event.description.html,
        image: event.logo?.url,
        url: event.url,
        id: event.id,
        start_date: event.start.local,
        original_event: event,
    };

    return transformedEvent;
};

export const validPlaces = {
    85977539: true, // New York City
    85923517: true, // Los Angeles
    102086957: true, // LA County
    404496273: true, // Chicago
    102087579: true, // San Francisco
    102080647: true, // Dallas
    102081111: true, // Harris County (Houston)
    102083603: true, // Fulton County (Atlanta)
    85936429: true, // Atlanta (city of atlanta)
    102082313: true, // Cobb County (North East Atlanta)
    102084889: true, // DC
    102085771: true, // Miami Dade County (Miami)
    102085847: true, // Orange County (Orlando)
    101750367: true, // London
};

export const mapToPreviewEvent = async (event: any) => {
    let places = {};
    if (!event.online_event) {
        const { latitude, longitude } = event.venue;
        places = await latLngToPlaces(latitude, longitude);
    }

    const fullStartTime = event.start?.utc;
    const fullEndTime = event.end?.utc;
    const startLocal = event.start?.local;
    const startDate = moment(fullStartTime).format('YYYY-MM-DD');
    const startTime = moment(fullStartTime).format('HH:mm');
    const endDate = moment(fullEndTime).format('YYYY-MM-DD');
    const endTime = moment(fullEndTime).format('HH:mm');

    const previewEvent = {
        name: event.name?.text,
        is_online_event: event.online_event,
        startLocal,
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime,
        image: {
            url: event.logo?.url || '',
        },
        primary_venue: {
            name: event.venue?.name || '',
            address: {
                localized_area_display:
                    event.venue?.localized_area_display || '',
            },
            places,
        },
        ticket_availability: {
            is_free: event.is_free,
            minimum_ticket_price: {
                value: event.ticket_availability?.minimum_ticket_price?.value,
                major_value:
                    event.ticket_availability?.minimum_ticket_price
                        ?.major_value,
            },
        },
        organizerId: event?.organizer?.id || null,
    };

    return previewEvent;
};

export const latLngToPlaces = async (latitude: string, longitude: string) => {
    try {
        const baseUrl = '/api/v3/geo/hierarchy_from_coordinates/';

        const queryParams = {
            latitude,
            longitude,
        };

        const results = await sdkRequest(formatUrl(baseUrl, queryParams));

        return results.places;
    } catch (e) {
        return {};
    }
};

export const checkEventLocationEligibility = (selectedEvent: any) => {
    if (selectedEvent.is_online_event) {
        return true;
    }

    const places = selectedEvent?.primary_venue?.places;

    if (!places) {
        return false;
    }

    const placeIds = Object.keys(places).map((key) => places[key].id);

    let eligible = false;

    for (const placeId of placeIds) {
        if (placeId in validPlaces) {
            eligible = true;
        }
    }

    return eligible;
};

export const fetchTotalFollowers = async (organizerId: string) => {
    if (!organizerId) {
        return 0;
    }
    const { num_followers } = await sdkRequest(
        `/api/v3/destination/organizers/${organizerId}/`,
    ).catch(() => {
        return { num_followers: 0 };
    });

    return num_followers || 0;
};
